// src/components/auth/RequireFeeAuth.jsx
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card } from "react-bootstrap";

import { useAuth } from "../../context/AuthContext";
import useSchool from "../../hooks/useSchool";

// Use the *same* helpers as FeeLogin
import {
  isFeeUnlocked,
  fetchFeeConfig,
} from "../../pages/school/fees/data/feesAuth";

export default function RequireFeeAuth({ children }) {
  const { user, isSchoolAdmin, loading: authLoading } = useAuth();
  const { school, loading: schoolLoading } = useSchool();

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const schoolId = school?.id || null;
  const uid = user?.uid || null;

  const [state, setState] = useState({ checking: true, blocked: false });

  useEffect(() => {
    let mounted = true;

    (async () => {
      // 1) Wait for both contexts to be ready
      if (authLoading || schoolLoading) return;

      // 2) Must be school admin
      if (!isSchoolAdmin) {
        if (mounted) setState({ checking: false, blocked: true });
        return;
      }

      // 3) Must have school & user
      if (!schoolId || !uid) {
        if (mounted) setState({ checking: false, blocked: true });
        return;
      }

      // 4) If already unlocked, allow
      if (isFeeUnlocked(schoolId, uid)) {
        if (mounted) setState({ checking: false, blocked: false });
        return;
      }

      // 5) If no pin configured, allow
      const cfg = await fetchFeeConfig(schoolId).catch(() => null);
      const hasPin = !!cfg?.pinHash;
      if (!hasPin) {
        if (mounted) setState({ checking: false, blocked: false });
        return;
      }

      // 6) Redirect to PIN page with return path
      navigate(`/school/fees/login?next=${encodeURIComponent(pathname)}`, {
        replace: true,
      });
    })();

    return () => {
      mounted = false;
    };
  }, [
    authLoading,
    schoolLoading,
    isSchoolAdmin,
    schoolId,
    uid,
    pathname,
    navigate,
  ]);

  if (state.checking) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="py-5 text-center text-muted">
          Checking access…
        </Card.Body>
      </Card>
    );
  }

  if (state.blocked) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="py-5 text-center">
          <h5 className="mb-2">Restricted</h5>
          <div className="text-muted">
            Only School Admins can access the Fees module.
          </div>
        </Card.Body>
      </Card>
    );
  }

  return children;
}
